import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  Text,
  VStack,
  Icon,
  useColorModeValue
} from '@chakra-ui/react';
import { AlertTriangle } from 'lucide-react';

interface SimpleSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal simples para informar ao usuário sobre assinatura expirada
 * Usado diretamente pelo interceptor de API
 */
const SimpleSubscriptionModal: React.FC<SimpleSubscriptionModalProps> = ({ isOpen, onClose }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const iconColor = useColorModeValue('red.500', 'red.300');
  
  const handleRenovar = () => {
    /* Vai para a aba Assinatura em Configurações, que é onde a cobrança vive desde a
       integração com o AbacatePay.
       Até 09/10/2026 este botão apontava para um portal da STRIPE fixo no código
       (`billing.stripe.com/p/login/...`), resquício de uma tentativa anterior: a conta
       não é usada por nenhuma parte do sistema e a API nunca emitiu o
       `SUBSCRIPTION_REQUIRED` que abre este modal. Mandar quem está em atraso para o
       portal de outro provedor seria oferecer um pagamento que não regulariza nada. */
    window.location.href = '/config';
    onClose();
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay backdropFilter="blur(5px)" />
      <ModalContent bg={bgColor}>
        <ModalHeader color="red.500">Assinatura Expirada</ModalHeader>
        
        <ModalBody>
          <VStack spacing={4} py={2}>
            <Icon as={AlertTriangle} boxSize={12} color={iconColor} />
            
            <Text textAlign="center">
              Sua assinatura expirou ou está pendente. Para continuar utilizando 
              todas as funcionalidades da plataforma, por favor renove sua assinatura.
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Depois
          </Button>
          <Button colorScheme="blue" onClick={handleRenovar}>
            Renovar Agora
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Exporta função para abrir o modal a partir de qualquer lugar
let openModalFn: (() => void) | null = null;
let closeModalFn: (() => void) | null = null;

export const setSubscriptionModalControls = (open: () => void, close: () => void) => {
  openModalFn = open;
  closeModalFn = close;
  
  // Expõe a função no objeto window para acesso global
  if (typeof window !== 'undefined') {
    window.openSubscriptionModal = open;
  }
};

export const showSubscriptionModal = () => {
  if (openModalFn) {
    openModalFn();
  }
};

export const hideSubscriptionModal = () => {
  if (closeModalFn) {
    closeModalFn();
  }
};

export default SimpleSubscriptionModal;
